/**
 * Cloudinary URL transformation utilities
 * Generates optimized image URLs with responsive sizing, format optimization, and quality settings
 */

export interface CloudinaryTransformOptions {
  width?: number;
  height?: number;
  quality?: "auto" | "auto:low" | "auto:eco" | "auto:good" | "auto:best" | number;
  format?: "auto" | "webp" | "avif" | "jpg" | "png";
  crop?: "fill" | "fit" | "scale" | "thumb" | "crop" | "pad";
  gravity?: "auto" | "face" | "faces" | "center" | "north" | "south" | "east" | "west";
  blur?: number;
  dpr?: number | "auto";
  removeBackground?: boolean; // Remove background using AI
}

/**
 * Check if a URL is a Cloudinary URL
 */
export function isCloudinaryUrl(url: string): boolean {
  return url.includes("res.cloudinary.com") || url.includes("cloudinary.com");
}

/**
 * Parse a Cloudinary URL into its components
 */
function parseCloudinaryUrl(url: string): {
  baseUrl: string;
  version: string;
  publicId: string;
  transformations: string;
} | null {
  // Match pattern: https://res.cloudinary.com/{cloud}/image/upload/{transformations}/{version}/{publicId}
  const regex = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload)(?:\/([^/]+))?(?:\/(v\d+))?\/(.+)$/;
  const match = url.match(regex);
  
  if (!match) return null;
  
  return {
    baseUrl: match[1],
    transformations: match[2] || "",
    version: match[3] || "",
    publicId: match[4],
  };
}

/**
 * Build transformation string from options
 */
function buildTransformString(options: CloudinaryTransformOptions): string {
  const transforms: string[] = [];
  
  if (options.width) transforms.push(`w_${options.width}`);
  if (options.height) transforms.push(`h_${options.height}`);
  if (options.quality) transforms.push(`q_${options.quality}`);
  if (options.format) transforms.push(`f_${options.format}`);
  if (options.crop) transforms.push(`c_${options.crop}`);
  if (options.gravity) transforms.push(`g_${options.gravity}`);
  if (options.blur) transforms.push(`e_blur:${options.blur}`);
  if (options.dpr) transforms.push(`dpr_${options.dpr}`);
  if (options.removeBackground) transforms.push(`e_background_removal`);
  
  return transforms.join(",");
}

/**
 * Get an optimized Cloudinary URL with specified transformations
 */
export function getOptimizedUrl(
  url: string,
  options: CloudinaryTransformOptions = {}
): string {
  if (!url) return url;
  
  // If not a Cloudinary URL, return as-is
  if (!isCloudinaryUrl(url)) {
    return url;
  }
  
  const parsed = parseCloudinaryUrl(url);
  if (!parsed) return url;
  
  // Default optimizations
  const defaultOptions: CloudinaryTransformOptions = {
    quality: "auto",
    format: "auto",
    ...options,
  };
  
  const transformString = buildTransformString(defaultOptions);
  const versionPart = parsed.version ? `/${parsed.version}` : "";
  
  return `${parsed.baseUrl}/${transformString}${versionPart}/${parsed.publicId}`;
}

/**
 * Get a blur placeholder URL (tiny, heavily blurred version)
 */
export function getBlurPlaceholderUrl(url: string): string {
  return getOptimizedUrl(url, {
    width: 20,
    quality: "auto:low",
    format: "auto",
    blur: 1000,
  });
}

/**
 * Get a thumbnail URL
 */
export function getThumbnailUrl(
  url: string,
  size: number = 200
): string {
  return getOptimizedUrl(url, {
    width: size,
    height: size,
    crop: "fill",
    gravity: "auto",
  });
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(
  url: string,
  widths: number[] = [320, 640, 768, 1024, 1280, 1536]
): string {
  if (!url || !isCloudinaryUrl(url)) return "";
  
  return widths
    .map((width) => {
      const optimizedUrl = getOptimizedUrl(url, { width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(", ");
}

/**
 * Generate sizes attribute for responsive images
 */
export function generateSizes(breakpoints: { [key: string]: string } = {}): string {
  const defaultBreakpoints = {
    "(max-width: 640px)": "100vw",
    "(max-width: 1024px)": "50vw",
    "default": "33vw",
  };
  
  const merged = { ...defaultBreakpoints, ...breakpoints };
  
  return Object.entries(merged)
    .map(([query, size]) => {
      if (query === "default") return size;
      return `${query} ${size}`;
    })
    .join(", ");
}

/**
 * Preload a critical image
 */
export function preloadImage(url: string, options?: CloudinaryTransformOptions): void {
  if (typeof window === "undefined") return;
  
  const optimizedUrl = getOptimizedUrl(url, options);
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = optimizedUrl;
  
  // Add srcset if it's a responsive image
  if (isCloudinaryUrl(url)) {
    const srcset = generateSrcSet(url);
    if (srcset) {
      link.setAttribute("imagesrcset", srcset);
      link.setAttribute("imagesizes", generateSizes());
    }
  }
  
  document.head.appendChild(link);
}
